import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { KeyboardEvent, useEffect, useRef, useState, useMemo } from 'react';

import type { ChatMessage as ChatMessageType } from '@/types/chatWidget';

import { useGetDocument } from '@/api/endpoints/document/useGetDocument';
import { useCommunicate } from '@/api/endpoints/simulation/useCommunicate';
import { Home01Icon } from '@/assets/icons/Home01Icon';
import { MessageChatCircleIcon } from '@/assets/icons/MessageChatCircleIcon';
import { XIcon } from '@/assets/icons/XIcon';
import { useAuth } from '@/context/AuthProvider';
import { useChatWidget } from '@/context/ChatWidgetContext';
import { usePropertyManager } from '@/context/PropertyManagerProvider';

import ChatMessage from './ChatMessage';

interface ChatResponse {
  email?: string;
  message?: string;
  urgent?: boolean;
  intent?: string;
  session_id?: string;
}

// Add type for chat tab
export type ChatTab = 'general' | 'documents';

// Extend ChatMessage type for tab support
interface ChatMessageWithType extends ChatMessageType {
  type?: ChatTab; // optional for backward compatibility
}

const ChatWindow: React.FC = (): React.ReactElement | null => {
  const theme = useTheme();
  const { state, closeChat, sendMessage, setSessionId } = useChatWidget();
  const { selectedPropertyLocation } = usePropertyManager();
  const { session } = useAuth();
  const communicateMutation = useCommunicate();
  const [message, setMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Fetch document data if documentId is present
  const fetchDocumentQuery = useGetDocument(state.documentId || '', session);

  // Set default tab based on whether we have a document
  const [activeTab, setActiveTab] = useState<ChatTab>(state.documentId ? 'documents' : 'general');

  // Update active tab when documentId changes
  useEffect(() => {
    if (state.documentId && activeTab === 'general') {
      setActiveTab('documents');
    }
  }, [state.documentId, activeTab]);

  // Memoized filtered messages for each tab
  const generalMessages = useMemo(
    () =>
      (state.messages as ChatMessageWithType[]).filter(
        msg => (msg.type ?? 'general') === 'general'
      ),
    [state.messages]
  );
  const documentMessages = useMemo(
    () => (state.messages as ChatMessageWithType[]).filter(msg => msg.type === 'documents'),
    [state.messages]
  );

  const handleSendMessage = async () => {
    if (message.trim() && selectedPropertyLocation) {
      setIsTyping(true);

      try {
        // First update UI with user message
        await sendMessage(message.trim(), 'user', activeTab);

        // Then send to API
        const response: ChatResponse = await communicateMutation.mutateAsync({
          message: message.trim(),
          ...(state.session_id && { session_id: state.session_id }),
          ...(selectedPropertyLocation?.id && {
            property_id: selectedPropertyLocation.id.toString(),
            location_id: selectedPropertyLocation.id.toString(),
          }),
          ...(state.documentId && { document_id: state.documentId }),
          chat_type: activeTab,
        });

        // Store session ID in context
        if (response.session_id) {
          setSessionId(response.session_id);
        }

        // Add AI response to chat if we get one
        if (response.email) {
          await sendMessage(response.email, 'system', activeTab);
        } else if (response.message) {
          await sendMessage(response.message, 'system', activeTab);
        }
      } catch (error) {
        // Error is already handled in the mutation
      } finally {
        setMessage('');
        setIsTyping(false);
      }
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [state.messages]);

  if (!state.isOpen) {
    return null;
  }

  const isMutationLoading = communicateMutation.status === 'pending';

  // Prepare message content for each tab to avoid nested ternaries
  let messageContent: React.ReactNode;
  if (activeTab === 'general') {
    if (generalMessages.length === 0) {
      messageContent = (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            textAlign: 'center',
            p: 3,
            gap: 1.5,
          }}
        >
          <Paper
            elevation={0}
            sx={{
              p: 2,
              mb: 1,
              borderRadius: 1.5,
              backgroundColor: theme.palette.common.white,
              width: '100%',
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 600,
                fontSize: '14px',
                lineHeight: 1.5,
              }}
            >
              Ask me anything about {selectedPropertyLocation?.name || 'your property'}
            </Typography>
          </Paper>
        </Box>
      );
    } else {
      messageContent = generalMessages.map(msg => <ChatMessage key={msg.id} message={msg} />);
    }
  } else if (activeTab === 'documents') {
    if (documentMessages.length === 0) {
      const documentName = fetchDocumentQuery?.data?.original_name;
      let placeholderText =
        "No document chat yet. I'll be able to chat with you about your documents.";
      if (documentName) {
        placeholderText = `Chat with me about ${documentName}`;
      }

      messageContent = (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            textAlign: 'center',
            p: 3,
            gap: 1.5,
          }}
        >
          <Paper
            elevation={0}
            sx={{
              p: 2,
              mb: 1,
              borderRadius: 1.5,
              backgroundColor: theme.palette.common.white,
              width: '100%',
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 600,
                fontSize: '14px',
                lineHeight: 1.5,
              }}
            >
              {placeholderText}
            </Typography>
          </Paper>
        </Box>
      );
    } else {
      messageContent = documentMessages.map(msg => <ChatMessage key={msg.id} message={msg} />);
    }
  }

  return (
    <Paper
      elevation={4}
      sx={{
        position: 'fixed',
        bottom: 24,
        right: 24,
        zIndex: 1040,
        width: { xs: 'calc(100% - 48px)', sm: 400 },
        maxWidth: 'calc(100% - 48px)',
        height: { xs: 'calc(100% - 96px)', sm: 700 },
        maxHeight: { xs: 'calc(100% - 96px)', sm: 700 },
        borderRadius: 2,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        fontFamily: 'Inter, sans-serif',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2.5,
          background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 50%, ${theme.palette.secondary.light} 100%)`,
          color: theme.palette.common.white,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          <Typography
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 700,
              fontSize: '24px',
              lineHeight: 1.2,
              opacity: 0.7,
              color: theme.palette.common.white,
            }}
          >
            Ask me anything about your property.
          </Typography>
          <Typography
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 700,
              fontSize: '16px',
              lineHeight: 1.2,
              color: theme.palette.common.white,
            }}
          >
            Seriously — rent, bills, repairs… I've got you.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton
            size="small"
            onClick={closeChat}
            sx={{
              color: theme.palette.common.white,
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              },
            }}
          >
            <XIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Messages */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          bgcolor: theme.palette.background.default,
          p: 2,
        }}
      >
        {messageContent}
        <div ref={messagesEndRef} />
      </Box>

      {/* Input */}
      <Box
        component="form"
        sx={{
          p: 2,
          backgroundColor: theme.palette.background.paper,
          borderTop: `1px solid ${theme.palette.divider}`,
        }}
        onSubmit={e => {
          e.preventDefault();
          handleSendMessage();
        }}
      >
        <TextField
          fullWidth
          multiline
          maxRows={4}
          placeholder="Type your message..."
          value={message}
          onChange={e => setMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          variant="outlined"
          disabled={isTyping || isMutationLoading}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              backgroundColor: theme.palette.common.white,
              fontFamily: 'Inter, sans-serif',
            },
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {isTyping || isMutationLoading ? (
                  <CircularProgress size={24} color="primary" />
                ) : (
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    disabled={!message.trim()}
                    onClick={() => handleSendMessage()}
                    sx={{
                      borderRadius: 5,
                      px: 2,
                      minWidth: 'auto',
                      backgroundColor: '#0057FF',
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: 600,
                      '&:hover': {
                        backgroundColor: '#334BFA',
                      },
                    }}
                  >
                    Send
                  </Button>
                )}
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Bottom Navigation (Tabs) */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          borderTop: '1px solid rgba(0, 0, 0, 0.05)',
          boxShadow: '0px 0px 25px 0px rgba(0, 0, 0, 0.05)',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            p: '5px 2px',
            flex: 1,
            cursor: 'pointer',
            backgroundColor: activeTab === 'general' ? '#F5F7FF' : 'transparent',
            borderBottom:
              activeTab === 'general' ? `3px solid ${theme.palette.primary.main}` : 'none',
            transition: 'background 0.2s',
          }}
          onClick={() => setActiveTab('general')}
        >
          <IconButton sx={{ color: activeTab === 'general' ? '#0057FF' : '#1A1A1A', p: 0.5 }}>
            <Home01Icon />
          </IconButton>
          <Typography
            variant="subtitle2"
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 600,
              fontSize: '14px',
              lineHeight: 1.5,
              color: activeTab === 'general' ? '#0057FF' : '#1A1A1A',
            }}
          >
            General
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            p: '5px 2px',
            flex: 1,
            cursor: 'pointer',
            backgroundColor: activeTab === 'documents' ? '#F5F7FF' : 'transparent',
            borderBottom:
              activeTab === 'documents' ? `3px solid ${theme.palette.primary.main}` : 'none',
            transition: 'background 0.2s',
          }}
          onClick={() => setActiveTab('documents')}
        >
          <IconButton sx={{ color: activeTab === 'documents' ? '#0057FF' : '#1A1A1A', p: 0.5 }}>
            <MessageChatCircleIcon />
          </IconButton>
          <Typography
            variant="body2"
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontSize: '14px',
              lineHeight: 1.5,
              color: activeTab === 'documents' ? '#0057FF' : '#1A1A1A',
              fontWeight: activeTab === 'documents' ? 600 : 400,
            }}
          >
            Documents
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
};

export default ChatWindow;
